/**
 * Custom cart drawer upsell.
 * <cart-upsell> is re-rendered by Dawn's cart-drawer section refresh on every cart change,
 * so connectedCallback fires each time and the recommendations always match the current cart.
 * The "+" button adds the recommended product in the same size as the t-shirt already in the cart
 * (same colour when the product has it, otherwise the first available colour in that size).
 * A small "other size / color" link keeps the product page one tap away.
 * Products that don't exist in that size link to their page instead.
 * Closed circle (snippets/cart-drawer-upsell.liquid: data-circle-*): when the first article of the cart belongs to the
 * circle collection (Holy Mountain Society) only the other products of that collection are offered; otherwise the
 * products of the circle are never offered.
 * Speed: during the page load (drawer closed) the recommendations are fetched once the page is idle, so they don't
 * compete with the photos of the page; after a cart change (page already loaded) they are fetched at once.
 */
const CDU_SIZE_NAMES = ['size', 'taille'];
const CDU_COLOR_NAMES = ['color', 'colour', 'couleur'];

function cduFormatMoney(price) {
  const cents =
    typeof price === 'string' ? (price.includes('.') ? Math.round(parseFloat(price) * 100) : parseInt(price, 10)) : price;
  const currency = window.Shopify?.currency?.active || 'EUR';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(
      cents / 100,
    );
  } catch (e) {
    return (cents / 100).toFixed(2);
  }
}

/**
 * Free shipping bar for carts in another currency than the shop's (USD, GBP...).
 * The EUR threshold is converted with Shopify's exchange rate, like Shopify converts the
 * FREESHIP discount minimum. Carts in the shop currency are rendered server-side in Liquid.
 */
class CartShipBar extends HTMLElement {
  connectedCallback() {
    const rate = parseFloat(window.Shopify?.currency?.rate || '0');
    const shopThreshold = parseInt(this.dataset.threshold, 10);
    const total = parseInt(this.dataset.total, 10);
    if (!rate || !shopThreshold || Number.isNaN(total)) {
      this.style.display = 'none';
      return;
    }

    const threshold = Math.round(shopThreshold * rate);
    const remaining = threshold - total;
    const progress = Math.min(100, Math.floor((total * 100) / threshold));

    const text = this.querySelector('.cdu-ship__text');
    text.replaceChildren();
    if (remaining <= 0) {
      const strong = document.createElement('strong');
      strong.textContent = "You've unlocked FREE shipping!";
      text.append('\u{1F389} ', strong);
      this.classList.add('cdu-ship--done');
    } else {
      const amount = document.createElement('strong');
      amount.textContent = cduFormatMoney(remaining);
      const label = document.createElement('strong');
      label.textContent = 'FREE shipping';
      text.append("You're ", amount, ' away from ', label);
    }

    this.querySelector('.cdu-ship__fill').style.width = `${progress}%`;
    this.querySelector('.cdu-ship__track').setAttribute('aria-valuenow', progress);
  }
}

if (!customElements.get('cart-ship-bar')) customElements.define('cart-ship-bar', CartShipBar);

class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.loaded) return;
    this.loaded = true;
    this.list = this.querySelector('.cdu-upsell__list');
    const pageLoaded = document.readyState === 'complete';
    const drawerOpen = this.closest('cart-drawer')?.classList.contains('active');
    if (pageLoaded || drawerOpen || !('requestIdleCallback' in window)) {
      this.load();
    } else {
      window.requestIdleCallback(() => this.load(), { timeout: 3000 });
    }
  }

  optionIndex(product, names) {
    return (product.options || []).findIndex((option) =>
      names.includes(String(typeof option === 'string' ? option : option.name).trim().toLowerCase()),
    );
  }

  // Returns the variant to add in one click, or null when the customer has to choose.
  pickVariant(product) {
    const available = product.variants.filter((variant) => variant.available);
    if (!available.length) return null;
    if (product.variants.length === 1) return available[0];

    const sizeIndex = this.optionIndex(product, CDU_SIZE_NAMES);
    const seedSize = this.dataset.seedSize;
    if (sizeIndex < 0 || !seedSize) return null;

    const sameSize = available.filter((variant) => variant[`option${sizeIndex + 1}`] === seedSize);
    if (!sameSize.length) return null;

    const colorIndex = this.optionIndex(product, CDU_COLOR_NAMES);
    const seedColor = this.dataset.seedColor;
    if (colorIndex >= 0 && seedColor) {
      const sameColor = sameSize.find((variant) => variant[`option${colorIndex + 1}`] === seedColor);
      if (sameColor) return sameColor;
    }
    return sameSize[0];
  }

  circleIds() {
    return (this.dataset.circleIds || '').split(',').filter(Boolean);
  }

  // Products of the circle collection, in the shape of the recommendations API (storefront collection JSON).
  async fetchCircleProducts() {
    const root = window.Shopify?.routes?.root || '/';
    const handle = this.dataset.circleHandle;
    if (!handle) return [];
    const response = await fetch(`${root}collections/${handle}/products.json?limit=50`);
    if (!response.ok) return [];
    return ((await response.json()).products || []).map((product) => ({
      ...product,
      available: (product.variants || []).some((variant) => variant.available),
      url: `${root}products/${product.handle}`,
      featured_image: product.images?.[0]?.src || null,
      price: product.variants?.[0]?.price,
    }));
  }

  async fetchRecommendations(seed, intent) {
    // Closed circle: an article of the circle only gets the other products of the circle
    if (this.dataset.seedInCircle === 'true') {
      return intent === 'complementary' ? this.fetchCircleProducts() : [];
    }
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}recommendations/products.json?product_id=${seed}&limit=10&intent=${intent}`);
    if (!response.ok) return [];
    const products = (await response.json()).products || [];
    const circle = this.circleIds();
    return circle.length ? products.filter((product) => !circle.includes(String(product.id))) : products;
  }

  async load() {
    const seed = this.dataset.productId;
    if (!seed || !this.list || !this.isConnected) return;
    const inCart = (this.dataset.cartProducts || '').split(',');
    const limit = parseInt(this.dataset.limit, 10) || 2;

    try {
      let products = await this.fetchRecommendations(seed, 'complementary');
      if (!products.length) products = await this.fetchRecommendations(seed, 'related');

      const entries = products
        .filter((product) => product.available && !inCart.includes(String(product.id)))
        .map((product) => ({ product, variant: this.pickVariant(product) }));
      // One-click products first.
      entries.sort((a, b) => (a.variant ? 0 : 1) - (b.variant ? 0 : 1));

      const shown = entries.slice(0, limit);
      if (!shown.length) return;

      shown.forEach(({ product, variant }) => this.list.appendChild(this.buildCard(product, variant)));
      this.hidden = false;
    } catch (error) {
      console.error('[cart-upsell] could not load recommendations', error);
    }
  }

  buildCard(product, variant) {
    const card = document.createElement('div');
    card.className = 'cdu-card';

    const imageUrl =
      variant?.featured_image?.src ||
      (typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.src);
    const img = document.createElement('img');
    img.className = 'cdu-card__img';
    img.alt = '';
    img.loading = 'lazy';
    img.width = 52;
    img.height = 52;
    if (imageUrl) img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}width=200`;
    card.appendChild(img);

    const info = document.createElement('div');
    info.className = 'cdu-card__info';
    const name = document.createElement('a');
    name.className = 'cdu-card__name';
    name.href = variant && product.variants.length > 1 ? `${product.url}?variant=${variant.id}` : product.url;
    name.textContent = product.title;
    info.appendChild(name);

    const hasChoices = product.variants.length > 1;

    if (variant && hasChoices) {
      const variantLine = document.createElement('p');
      variantLine.className = 'cdu-card__variant';
      variantLine.textContent = [variant.option1, variant.option2, variant.option3]
        .filter(Boolean)
        .sort((a, b) => (a === this.dataset.seedSize ? -1 : b === this.dataset.seedSize ? 1 : 0))
        .join(' · ');
      info.appendChild(variantLine);
    }

    const price = document.createElement('p');
    price.className = 'cdu-card__price';
    price.textContent = cduFormatMoney(variant ? variant.price : product.price);
    info.appendChild(price);

    if (variant && hasChoices) {
      const other = document.createElement('a');
      other.className = 'cdu-card__options';
      other.href = product.url;
      other.textContent = this.dataset.otherLabel || 'Other size / color';
      info.appendChild(other);
    }
    card.appendChild(info);

    if (variant) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--secondary cdu-card__add';
      button.textContent = '+';
      button.setAttribute('aria-label', `${this.dataset.addLabel || 'Add'} ${product.title}`);
      button.addEventListener('click', () => this.add(card, button, variant.id));
      card.appendChild(button);
    } else {
      const link = document.createElement('a');
      link.className = 'button button--secondary';
      link.href = product.url;
      link.textContent = this.dataset.optionsLabel || 'Options';
      card.appendChild(link);
    }

    return card;
  }

  async add(card, button, variantId) {
    button.disabled = true;
    button.classList.add('loading');
    card.querySelector('.cdu-card__error')?.remove();
    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (data.status) throw new Error(data.description || data.message || 'error');
      const drawer = document.querySelector('cart-drawer');
      drawer.classList.remove('is-empty');
      drawer.renderContents(data);
    } catch (error) {
      button.disabled = false;
      button.classList.remove('loading');
      const message = document.createElement('p');
      message.className = 'cdu-card__error';
      message.setAttribute('role', 'alert');
      message.textContent = this.dataset.errorLabel || 'Could not add this item.';
      card.appendChild(message);
    }
  }
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);

/**
 * Drawer guard: while the cart drawer is open, hide any third-party floating widget
 * (sales popups, chat/AI bubbles, promo popups...) that sits on top of the drawer panel,
 * and restore it when the drawer closes. Works for widgets we don't know yet, including
 * shadow-DOM ones. The cookie consent banner is never hidden.
 */
(() => {
  if (window.__cduDrawerGuard) return;
  window.__cduDrawerGuard = true;

  const NEVER_HIDE = ['shopify-pc__banner'];
  const hiddenElements = new Set();
  let sweepTimer = null;

  const topLevelOf = (element) => {
    let node = element;
    while (node.parentElement && node.parentElement !== document.body && node.parentElement !== document.documentElement) {
      node = node.parentElement;
    }
    return node;
  };

  const isProtected = (element) =>
    NEVER_HIDE.some((id) => element.id === id || element.querySelector?.(`#${id}`));

  const sweep = () => {
    const drawer = document.querySelector('cart-drawer');
    const panel = drawer?.querySelector('.drawer__inner');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    for (let x = rect.left + 8; x < rect.right; x += 36) {
      for (let y = rect.top + 8; y < rect.bottom; y += 36) {
        const hit = document.elementFromPoint(x, y);
        if (!hit || hit === document.documentElement || hit === document.body || drawer.contains(hit)) continue;
        const top = topLevelOf(hit);
        if (top === drawer || top.contains(drawer) || hiddenElements.has(top) || isProtected(top)) continue;
        top.dataset.cduPrevVisibility = top.style.visibility;
        top.style.setProperty('visibility', 'hidden', 'important');
        hiddenElements.add(top);
      }
    }
  };

  const restore = () => {
    hiddenElements.forEach((element) => {
      element.style.visibility = element.dataset.cduPrevVisibility || '';
      delete element.dataset.cduPrevVisibility;
    });
    hiddenElements.clear();
  };

  const update = () => {
    const open = document.querySelector('cart-drawer')?.classList.contains('active');
    if (open && !sweepTimer) {
      sweep();
      sweepTimer = setInterval(sweep, 700);
    } else if (!open && sweepTimer) {
      clearInterval(sweepTimer);
      sweepTimer = null;
      restore();
    }
  };

  const start = () => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    new MutationObserver(update).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    update();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
